/**
 * CypAdhdMsePanel — Mental State Examination step of the CYP ADHD Clinical
 * Interview.
 *
 * Renders one of two configs, chosen by cypAdhdFlow.mse: the adult mirror
 * (cypAdhdMseConfig, the default) or the child-specific Clinical Observation
 * (cypAdhdClinicalObservationConfig), which uses child-specific wording
 * and clinical-observation categories.
 *
 * In the adult mirror, structure and option wording are UNCHANGED FROM ADULT
 * (cypAdhdMseConfig transcribes the adult MSE verbatim). The STRUCTURE of the
 * markup is the adult MSE's (6_clinicalInterview/10_MSE.js) — a card per
 * subsection, a bulk "Yes to all?" control in the card header, a Category /
 * Age appropriate? / Details table, the pill-pair Yes/No toggle, subcategory selects
 * as indented rows, a notes card — but the VISUAL vocabulary is the Adult
 * Autism History's (HistoryRiskAssessmentPanel), because the CYP CI page is a
 * port of the autism History chrome and the MSE must read as part of it:
 *
 *   - gray-50 rounded-lg cards with a gray-300 border and a light shadow,
 *     stacked with space-y-6, numbered "{section}.{n} {Title}"
 *   - a 30% / 20% / 50% header row with the bulk button as a small
 *     semibold text-xs outline control
 *   - a text-sm gray-600 table head over gray-200 row dividers, py-3 cells,
 *     semibold text-sm category labels
 *   - rounded-l/r-full pill toggles in semibold text-sm (green-200 / red-200
 *     when chosen) — colour never carries the meaning alone, the label does
 *   - the Details select ALWAYS rendered, showing "Selection can only be made
 *     by assigned clinician" to a clinician who cannot edit
 *   - notes cards with a font-bold text-lg title and a text-sm gray-600 lead
 *
 * Behaviour deliberately mirrors adult too: "Yes to all" and the per-category
 * toggle both reuse buildMergedSelection from utils/selectionMergeUtils, the
 * same helper the adult page uses, so clinician-typed custom entries survive a
 * toggle and only predefined options are swapped.
 *
 * Data lands on the ci stage document under "mse" via the parent-supplied
 * onMseUpdate (deep-merged, same pattern as the risk panel):
 *   mse.{subsectionVarName}.{categoryName} = { isAppropriateSelection,
 *   selectionDetails, subcategories? }, mse.mood.description for the free-text
 *   Mood subsection, and mse.additionalNotes for the closing notes card.
 *
 * One deliberate difference from adult, recorded on ASMT-18: the adult page
 * opens a confirmation dialog when a toggle would discard subcategory
 * selections. Here the toggle reseeds directly. Custom entries are preserved
 * either way (buildMergedSelection), so what a confirm would protect is only
 * the Hallucinations Type/Quality/Nature selects, one click from re-entry.
 */

import React, { useCallback, useMemo, useState } from 'react';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import AutoSaveCreatable from '../../../../components/AutoSaveCreatable';
import StructuredNotesEditor from '../../12_observation/components/StructuredNotesEditor';
import { CYP_NOTE_TAGGING_ENABLED, CYP_NOTE_TAG_OPTIONS } from '../../../../constants/cypAdhdNoteTagOptions';
import { normaliseNotesValue } from '../../../../utils/autismReviewData';
import { moveNoteBetweenArrays } from '../../../../utils/structuredNotesMove';

/**
 * Stable identity for each notes box on this page.
 *
 * StructuredNotesEditor compares the dragged note's `sourceDomainId` against
 * its own `domainId` to tell a same-box reorder from a cross-box move. With
 * both undefined every drag took the reorder path and silently did nothing,
 * so a note dragged from Mood into Clinical Notes just vanished from the
 * cursor. These ids make the two cases distinguishable.
 */
const mseNotesDomainId = (varName) => `mse-${varName}`;
const MSE_ADDITIONAL_NOTES_DOMAIN_ID = 'mse-additionalNotes';

/**
 * Notes value -> the editor's array shape, recovering the index-keyed object
 * a merge bug briefly stored.
 *
 * Until 1 Sept, a note typed into Additional Notes was merged as if the array
 * were a category map, so it landed as { '0': note, '1': note } on the stage
 * document. normaliseNotesValue reads that as empty, so those notes were
 * invisible. The merge is fixed; this recovers anything already written rather
 * than leaving a clinician's typed observations silently unreadable. Local to
 * CYP on purpose — the shared helper is used by autism and stays untouched.
 */
function recoverNotesValue(raw) {
  const normalised = normaliseNotesValue(raw);
  if (normalised.length > 0) return normalised;
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const recovered = Object.keys(raw)
      .filter((key) => /^\d+$/.test(key))
      .sort((a, b) => Number(a) - Number(b))
      .map((key) => raw[key])
      .filter((note) => note && typeof note === 'object' && typeof note.text === 'string');
    if (recovered.length > 0) return recovered;
  }
  return normalised;
}
import getScrollablePortalTarget from '../../../../utils/getScrollablePortalTarget';
import { buildMergedSelection } from '../../../../utils/selectionMergeUtils';
import InfoTooltip from '../../../../components/InfoTooltip';
import { CYP_MSE_SUBSECTIONS } from '../config/cypAdhdMseConfig';

const SELECT_PORTAL_PROPS = {
  classNamePrefix: 'select',
  menuPortalTarget: getScrollablePortalTarget(),
  menuPosition: 'fixed',
};

// Mirrors the adult page's react-select styles so the Details cell matches.
const SELECT_STYLES = {
  menu: (base) => ({ ...base, textAlign: 'left' }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (base, state) => ({
    ...base,
    textAlign: 'left',
    backgroundColor: state.isSelected ? 'lightgray' : base.backgroundColor,
  }),
};

const toOption = (value) => ({ value, label: value });

/** Card surface — the autism History section panel (see file header). */
const PANEL_CLASS = 'bg-gray-50 rounded-lg border border-gray-300 text-left p-6 shadow-sm';

/** The pill-toggle base classes (autism HistoryRiskAssessmentPanel). */
const PILL_BASE = 'py-1 px-3 border font-semibold text-sm transition-all duration-200';
const PILL_ON = 'border-gray-600 border-2';
const PILL_OFF = 'bg-white border-gray-400';

const detailsPlaceholder = (canEdit) => (canEdit
  ? 'Select options or write your own..'
  : 'Selection can only be made by assigned clinician');

/**
 * The adult pill pair: two half-rounded buttons that read as one control.
 * Colour alone never carries the meaning — the label inside each half does.
 *
 * The accessible NAME stays "Yes"/"No" (as on the adult page) and the category
 * is supplied by aria-describedby pointing at the row's first cell. Putting the
 * category in the name instead would read "Rapport typical", losing the word
 * the clinician actually sees on the button.
 */
function YesNoPills({ value, onSelect, disabled, describedBy }) {
  return (
    <>
      <button
        type="button"
        disabled={disabled}
        aria-describedby={describedBy}
        aria-pressed={value === true}
        onClick={() => onSelect(true)}
        className={`${PILL_BASE} rounded-l-full ${value === true
          ? `bg-green-200 ${PILL_ON}`
          : PILL_OFF
          }`}
      >
        Yes
      </button>
      <button
        type="button"
        disabled={disabled}
        aria-describedby={describedBy}
        aria-pressed={value === false}
        onClick={() => onSelect(false)}
        className={`${PILL_BASE} rounded-r-full ${value === false
          ? `bg-red-200 ${PILL_ON}`
          : PILL_OFF
          }`}
      >
        No
      </button>
    </>
  );
}

/** True when a category has subcategory dropdown answers that a Yes/No
    change would throw away. The adult MSE warns only for these — the main
    details dropdown's predefined options are swapped by the toggle, and
    anything the clinician typed is kept. */
function hasSubcategorySelections(stored) {
  const subs = stored?.subcategories;
  if (!subs || typeof subs !== 'object') return false;
  return Object.values(subs).some((byName) => (
    byName && typeof byName === 'object' && Object.values(byName).some(
      (sub) => Array.isArray(sub?.selectionDetails) && sub.selectionDetails.length > 0,
    )
  ));
}

/**
 * The adult MSE's confirm step (10_MSE.js). Shown before a Yes/No change or
 * "Yes to all?" that would discard subcategory answers. Cancel leaves the
 * row as it was.
 */
function MseChangeConfirmDialog({ pending, onCancel, onConfirm }) {
  return (
    <AlertDialog.Root
      open={Boolean(pending)}
      onOpenChange={(open) => { if (!open) onCancel(); }}
    >
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
        <AlertDialog.Content className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg p-6 w-[420px] max-w-[90vw] z-50 shadow-xl">
          <AlertDialog.Title className="text-lg font-bold mb-2">
            Confirm Selection Change
          </AlertDialog.Title>
          <AlertDialog.Description className="text-sm text-gray-600 mb-5">
            {pending?.description}
          </AlertDialog.Description>
          <div className="flex justify-end gap-3">
            <AlertDialog.Cancel asChild>
              <button
                type="button"
                className="py-2 px-4 font-semibold border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </AlertDialog.Cancel>
            <AlertDialog.Action asChild>
              <button
                type="button"
                onClick={onConfirm}
                className="py-2 px-4 font-semibold rounded-md bg-primary text-white hover:bg-primary/90 transition-colors"
              >
                Continue
              </button>
            </AlertDialog.Action>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}

function CategoryRows({
  subsectionVar, category, data, onMseUpdate, canEdit, onRequestConfirm,
}) {
  const stored = data?.[category.name] || {};
  const isAppropriate = stored.isAppropriateSelection ?? null;
  // Memoised because `|| []` would hand back a fresh array on every render,
  // rebuilding the toggle callback each time for no reason.
  const selectionDetails = useMemo(
    () => stored.selectionDetails || [],
    [stored.selectionDetails],
  );

  // Which side's option list the Details select offers. Before any decision is
  // recorded the adult page offers the "No" list (its `appropriate` is
  // undefined, which is falsy), and the dropdown stays usable — a clinician
  // can type or pick before pressing Yes or No. A details-only aspect
  // (if any) offers its own flat `options` list.
  let sideOptions = isAppropriate ? category.yesOptions : category.noOptions;
  if (category.detailsOnly) sideOptions = category.options;

  const handleToggle = useCallback((clicked) => {
    if (!canEdit) return;
    // Adult 10_MSE.js: clicking the pill that is already on clears it back
    // to unanswered. Yes seeds that side's first option into the details
    // dropdown; No and a clear seed nothing. Typed entries survive either way.
    const newValue = isAppropriate === clicked ? null : clicked;
    const defaultValue = newValue ? (category.yesOptions?.[0] ?? null) : null;
    const merged = buildMergedSelection(
      selectionDetails.map(toOption),
      category.yesOptions || [],
      category.noOptions || [],
      defaultValue,
    );
    const apply = () => onMseUpdate({
      [subsectionVar]: {
        [category.name]: {
          isAppropriateSelection: newValue,
          selectionDetails: merged.map((o) => o.value),
          subcategories: {},
        },
      },
    });
    // Same guard as the adult page: only subcategory answers (Hallucinations
    // Type/Quality/Nature) are warned about. The details dropdown itself is
    // rewritten without a dialog, and custom typed notes are kept.
    if (hasSubcategorySelections(stored)) {
      onRequestConfirm(
        'Changing this selection will reset the sub-category details for this category. Your custom notes will be kept.',
        apply,
      );
      return;
    }
    apply();
  }, [canEdit, isAppropriate, category, subsectionVar, onMseUpdate, selectionDetails,
    stored, onRequestConfirm]);

  const handleSelectionChange = useCallback((selected) => {
    if (!canEdit) return;
    onMseUpdate({
      [subsectionVar]: {
        [category.name]: {
          selectionDetails: (selected || []).map((o) => o.value),
        },
      },
    });
  }, [canEdit, subsectionVar, category.name, onMseUpdate]);

  const handleSubcategoryChange = useCallback((optionName, subName, selected) => {
    if (!canEdit) return;
    onMseUpdate({
      [subsectionVar]: {
        [category.name]: {
          subcategories: {
            [optionName]: {
              [subName]: { selectionDetails: (selected || []).map((o) => o.value) },
            },
          },
        },
      },
    });
  }, [canEdit, subsectionVar, category.name, onMseUpdate]);

  const revealedSubcategories = category.subcategories
    ? selectionDetails.filter((value) => category.subcategories[value])
    : [];

  // Stable id so the Yes/No pills can point at the category cell for their
  // description. Slugged because category names contain spaces, "&" and ":".
  const cellId = `mse-${subsectionVar}-${category.name.replace(/[^a-zA-Z0-9]+/g, '-')}`;

  return (
    <>
      <tr className="border-t border-gray-200">
        <td className="py-3 pr-4 text-sm align-top" id={cellId}>
          <span className="font-semibold text-gray-800">{category.name}</span>
          {category.hint && (
            <span className="block text-gray-600">{category.hint}</span>
          )}
        </td>
        <td className="py-3 text-center align-top">
          {!category.detailsOnly && (
            <YesNoPills
              value={isAppropriate}
              onSelect={handleToggle}
              disabled={!canEdit}
              describedBy={cellId}
            />
          )}
        </td>
        <td className="py-3 align-top">
          <AutoSaveCreatable
            isMulti
            aria-label={`${category.name} details`}
            isDisabled={!canEdit}
            styles={SELECT_STYLES}
            options={(sideOptions || []).map(toOption)}
            value={selectionDetails.map(toOption)}
            onChange={handleSelectionChange}
            formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
            placeholder={detailsPlaceholder(canEdit)}
            className="basic-multi-select text-sm"
            {...SELECT_PORTAL_PROPS}
          />
        </td>
      </tr>

      {/* Perception: Hallucinations Type/Quality/Nature, as indented rows on
          the adult page rather than a nested block. */}
      {revealedSubcategories.map((optionName) => (
        Object.entries(category.subcategories[optionName]).map(([subName, options]) => {
          const subStored = stored.subcategories?.[optionName]?.[subName]?.selectionDetails || [];
          return (
            <tr key={`${category.name}-${optionName}-${subName}`} className="border-t border-gray-200">
              <td className="py-3 pl-8 pr-4 font-semibold text-sm text-gray-800 align-top">{optionName} - {subName}</td>
              <td colSpan="2" className="py-3 align-top">
                <AutoSaveCreatable
                  isMulti
                  aria-label={`${optionName} ${subName}`}
                  isDisabled={!canEdit}
                  styles={SELECT_STYLES}
                  options={options.map(toOption)}
                  value={subStored.map(toOption)}
                  onChange={(selected) => handleSubcategoryChange(optionName, subName, selected)}
                  formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
                  placeholder={detailsPlaceholder(canEdit)}
                  className="basic-multi-select text-sm"
                  {...SELECT_PORTAL_PROPS}
                />
              </td>
            </tr>
          );
        })
      ))}
    </>
  );
}

function ClickthroughSubsection({
  subsection, numberLabel, data, onMseUpdate, canEdit, decisionLabel, decisionTooltip,
}) {
  const [pendingChange, setPendingChange] = useState(null);
  const askConfirm = useCallback((description, apply) => {
    setPendingChange({ description, apply });
  }, []);
  // A details-only aspect has no Yes/No, so "Yes to all" neither answers it
  // nor waits on it.
  const answerableCategories = useMemo(
    () => subsection.categories.filter((category) => !category.detailsOnly),
    [subsection.categories],
  );
  // Disable the bulk action when every category is already Yes, so the control
  // never looks available while doing nothing (adult behaviour).
  const allAlreadyYes = answerableCategories.length > 0 && answerableCategories.every(
    (category) => data?.[category.name]?.isAppropriateSelection === true,
  );
  const yesToAllDisabled = !canEdit || allAlreadyYes;

  const handleYesToAll = useCallback(() => {
    if (yesToAllDisabled) return;
    const categoriesUpdate = {};
    answerableCategories.forEach((category) => {
      const existing = (data?.[category.name]?.selectionDetails || []).map(toOption);
      const merged = buildMergedSelection(
        existing,
        category.yesOptions || [],
        category.noOptions || [],
        category.yesOptions?.[0] ?? null,
      );
      categoriesUpdate[category.name] = {
        isAppropriateSelection: true,
        selectionDetails: merged.map((o) => o.value),
        subcategories: {},
      };
    });
    const apply = () => onMseUpdate({ [subsection.varName]: categoriesUpdate });
    const wouldLoseSubcategories = answerableCategories.some(
      (category) => hasSubcategorySelections(data?.[category.name]),
    );
    if (wouldLoseSubcategories) {
      askConfirm(
        'Setting all categories to "Yes" will reset sub-category selections to their defaults. Your custom notes will be kept.',
        apply,
      );
      return;
    }
    apply();
  }, [yesToAllDisabled, answerableCategories, subsection.varName, data, onMseUpdate, askConfirm]);

  return (
    <div className={PANEL_CLASS}>
      {/* Header row: title | bulk control | spacer, on the autism risk
          panel's 30/20/50 split so the bulk button sits over the decision
          column of the table beneath it. */}
      <div className="flex justify-between items-center mb-4">
        <div className="w-[30%] min-w-0">
          <h4 className="font-bold text-lg">{numberLabel} {subsection.title}</h4>
        </div>
        <div className="w-[20%] flex justify-center">
          <button
            type="button"
            onClick={handleYesToAll}
            className={`py-1.5 px-3 rounded-md shadow-sm font-semibold text-xs text-gray-900 bg-white border border-gray-400 transition-all duration-100 ${
              yesToAllDisabled
                ? 'opacity-60 cursor-not-allowed'
                : 'hover:bg-green-700 hover:text-white hover:border-green-700'
            }`}
            disabled={yesToAllDisabled}
          >
            Yes to all?
          </button>
        </div>
        <div className="w-[50%]"></div>
      </div>
      {subsection.tooltipText && (
        <p className="text-sm text-gray-600 mb-4">{subsection.tooltipText}</p>
      )}
      <div className="overflow-x-auto">
        <table className="w-full table-auto min-w-[640px]">
          <thead>
            <tr className="text-sm text-gray-600 border-t border-gray-200">
              <th className="text-left font-semibold py-2 w-[30%]">Category</th>
              <th className="text-center font-semibold py-2 w-[20%]">
                <span className="inline-flex items-center justify-center gap-1">
                  <span>{decisionLabel}</span>
                  {decisionTooltip && (
                    <InfoTooltip
                      content={decisionTooltip}
                      label={`Definition of ${decisionLabel.replace(/\?$/, '')}`}
                      maxWidth="320px"
                    />
                  )}
                </span>
              </th>
              <th className="text-left font-semibold py-2 w-[50%]">Details</th>
            </tr>
          </thead>
          <tbody>
            {subsection.categories.map((category) => (
              <CategoryRows
                key={category.name}
                subsectionVar={subsection.varName}
                category={category}
                data={data}
                onMseUpdate={onMseUpdate}
                canEdit={canEdit}
                onRequestConfirm={askConfirm}
              />
            ))}
          </tbody>
        </table>
      </div>
      <MseChangeConfirmDialog
        pending={pendingChange}
        onCancel={() => setPendingChange(null)}
        onConfirm={() => {
          pendingChange?.apply();
          setPendingChange(null);
        }}
      />
    </div>
  );
}

function FreeTextSubsection({
  subsection, numberLabel, data, onMseUpdate, canEdit, onMoveNoteIn, moveTargets,
}) {
  const value = data?.description;
  return (
    <div className={PANEL_CLASS}>
      <h4 className="font-bold text-lg mb-2">{numberLabel} {subsection.title}</h4>
      {subsection.tooltipText && (
        <p className="text-sm text-gray-600 mb-4">{subsection.tooltipText}</p>
      )}
      {subsection.tooltipLines && (
        <div className="text-sm text-gray-600 mb-4 space-y-1">
          {subsection.tooltipLines.map((line) => <p key={line}>{line}</p>)}
        </div>
      )}
      <div className="flex flex-col">
        <div className="flex gap-1 mb-2">
          <h4 className="text-gray-700 text-sm font-semibold">{subsection.freeTextLabel}</h4>
        </div>
        {/* The CYP bullet-point smart-notes box (ASMT-29 + Sai, 28 Aug: the
            MSE Mood panel takes structured notes, not a plain textarea). The
            backend flattens the array with its source tags. */}
        <StructuredNotesEditor
          notes={recoverNotesValue(value)}
          onChange={(notes) => canEdit && onMseUpdate({
            [subsection.varName]: { description: notes },
          })}
          domainId={mseNotesDomainId(subsection.varName)}
          onMoveNoteIn={onMoveNoteIn}
          moveTargets={moveTargets}
          currentMoveId={mseNotesDomainId(subsection.varName)}
          onMoveNoteTo={onMoveNoteIn}
          placeholder="Add a note, then press Enter to save each point"
          enableCriteriaTagging={CYP_NOTE_TAGGING_ENABLED}
          tagOptions={CYP_NOTE_TAG_OPTIONS}
          readOnly={!canEdit}
        />
      </div>
    </div>
  );
}

export default function CypAdhdMsePanel({
  mseData = {},
  onMseUpdate,
  canEdit,
  // Which MSE to render: the adult mirror by default, or the child-specific
  // Clinical Observation (cypAdhdClinicalObservationConfig) with its intro
  // and decision-column label.
  subsections = CYP_MSE_SUBSECTIONS,
  intro = null,
  decisionLabel = 'Age appropriate?',
  // Full wording behind the column heading (Clinical Observation: "Age
  // appropriate?" means "Within expected range for age and developmental
  // level?"). Omitted on the adult mirror, whose heading is already the
  // whole question.
  decisionTooltip = null,
  title = 'Mental State Examination',
}) {
  // Every notes box on this page, for the Move picker. Drag-and-drop alone is
  // not reachable by keyboard, so the picker is what makes moving a note
  // accessible — the same reason adult autism offers both.
  const moveTargets = useMemo(() => {
    const targets = subsections
      .filter((subsection) => subsection.type === 'freeText')
      .map((subsection, index) => ({
        id: mseNotesDomainId(subsection.varName),
        label: subsection.freeTextLabel || subsection.subsectionTitle || `Subsection ${index + 1}`,
        group: title,
      }));
    targets.push({
      id: MSE_ADDITIONAL_NOTES_DOMAIN_ID,
      label: 'Clinical Notes',
      group: title,
    });
    return targets;
  }, [subsections, title]);

  /**
   * Move one note from one box on this page into another, whether by drag or
   * by the Move picker. Both boxes are written in a single onMseUpdate so the
   * note can never exist in both places (or in neither) if the write fails
   * part-way.
   */
  const handleMoveNoteIn = useCallback((sourceDomainId, noteId, targetDomainId, insertAt, notePatch) => {
    if (!canEdit) return;
    if (!sourceDomainId || !targetDomainId || sourceDomainId === targetDomainId) return;

    const varNameFor = (domainId) => (
      domainId === MSE_ADDITIONAL_NOTES_DOMAIN_ID
        ? null
        : subsections.find((sub) => mseNotesDomainId(sub.varName) === domainId)?.varName
    );
    const readBox = (domainId) => (
      domainId === MSE_ADDITIONAL_NOTES_DOMAIN_ID
        ? recoverNotesValue(mseData?.additionalNotes)
        : recoverNotesValue(mseData?.[varNameFor(domainId)]?.description)
    );
    const writeFor = (domainId, notes) => (
      domainId === MSE_ADDITIONAL_NOTES_DOMAIN_ID
        ? { additionalNotes: notes }
        : { [varNameFor(domainId)]: { description: notes } }
    );

    const sourceVar = varNameFor(sourceDomainId);
    const targetVar = varNameFor(targetDomainId);
    if (sourceDomainId !== MSE_ADDITIONAL_NOTES_DOMAIN_ID && !sourceVar) return;
    if (targetDomainId !== MSE_ADDITIONAL_NOTES_DOMAIN_ID && !targetVar) return;

    const result = moveNoteBetweenArrays(
      readBox(sourceDomainId), readBox(targetDomainId), noteId, insertAt, notePatch,
    );
    if (!result) return;

    onMseUpdate({
      ...writeFor(sourceDomainId, result.source),
      ...writeFor(targetDomainId, result.target),
    });
  }, [canEdit, mseData, onMseUpdate, subsections]);

  return (
    // Cards stack with space-y-6, as the autism History's panels do.
    <div className="space-y-6">
      {intro && (
        <div className="text-left text-gray-700 space-y-2">
          {intro.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
        </div>
      )}
      {/* Clinical Notes first (UAT feedback, 8 Oct): the clinician's own
          observations go at the top of the MSE so they can be written while
          the examination is happening, before working through the decision
          panels below. */}
      <div className={PANEL_CLASS}>
        <h4 className="font-bold text-lg mb-2">Clinical Notes</h4>
        <p className="text-sm text-gray-600 mb-4">
          Record any observations, comments or relevant information from the
          examination.
        </p>
        <div className="flex flex-col">
          <StructuredNotesEditor
            notes={recoverNotesValue(mseData?.additionalNotes)}
            onChange={(notes) => canEdit && onMseUpdate({ additionalNotes: notes })}
            domainId={MSE_ADDITIONAL_NOTES_DOMAIN_ID}
            onMoveNoteIn={handleMoveNoteIn}
            moveTargets={moveTargets}
            currentMoveId={MSE_ADDITIONAL_NOTES_DOMAIN_ID}
            onMoveNoteTo={handleMoveNoteIn}
            placeholder="Add a note, then press Enter to save each point"
            enableCriteriaTagging={CYP_NOTE_TAGGING_ENABLED}
            tagOptions={CYP_NOTE_TAG_OPTIONS}
            readOnly={!canEdit}
          />
        </div>
      </div>

      {subsections.map((subsection, index) => {
        // Panels are numbered 1, 2, 3 in the order they appear on this step,
        // the same way the Risk Assessment sections are. The CI section
        // number is not prefixed (no "9.1").
        const numberLabel = `${index + 1}.`;
        return subsection.type === 'freeText' ? (
          <FreeTextSubsection
            key={subsection.varName}
            subsection={subsection}
            numberLabel={numberLabel}
            data={mseData?.[subsection.varName]}
            onMseUpdate={onMseUpdate}
            canEdit={canEdit}
            onMoveNoteIn={handleMoveNoteIn}
            moveTargets={moveTargets}
          />
        ) : (
          <ClickthroughSubsection
            key={subsection.varName}
            subsection={subsection}
            numberLabel={numberLabel}
            data={mseData?.[subsection.varName]}
            onMseUpdate={onMseUpdate}
            canEdit={canEdit}
            decisionLabel={decisionLabel}
            decisionTooltip={decisionTooltip}
          />
        );
      })}
    </div>
  );
}
